import { ClipboardCopyIcon, PlusIcon, TrashIcon } from "@heroicons/react/outline";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { useTranslation } from "react-i18next";
import { deleteData, getData, postData } from "../backend";
import { useModal } from "../contexts";
import ErrorModal from "./ErrorModal";

interface ApiToken {
  id: string;
  token: string;
  createdAt: string;
}

function SettingsDeveloper() {
  const { t } = useTranslation();
  const { openModal } = useModal();

  const [token, setToken] = useState<ApiToken | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  useEffect(() => {
    getData("/auth/developer-token/")
      .then((data) => setToken(data))
      .catch(() => { })
      .finally(() => setIsLoading(false));
  }, []);

  function handleCopied() {
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  }

  async function handleCreateToken() {
    setIsCreating(true);
    try {
      const data = await postData("/auth/developer-token/");
      setToken(data);
    } catch (error) {
      openModal(<ErrorModal details={error} />);
    }
    setIsCreating(false);
  }

  async function handleRevokeToken() {
    if (!token) return;
    setIsRevoking(true);
    try {
      await deleteData(`/auth/developer-token/`);
      setToken(null);
    } catch (error) {
      openModal(<ErrorModal details={error} />);
    }
    setIsRevoking(false);
  }

  return (
    <div className="flex flex-col p-8 h-[calc(100vh-8rem)]">
      <div className="font-gilroy font-semibold text-lg mb-2">{t("Developer")}</div>
      <div className="text-sm text-gray-500 mb-8">
        {t("Generate access tokens to use the Portant API programmatically.")}{" "}
        <a href="https://docs.portant.co/portant-docs/portant-developers/" target="_blank" rel="noopener noreferrer" className="text-blue hover:underline">
          {t("View documentation")}
        </a>
      </div>

      {/* Token display / generation */}
      <div className="flex flex-col w-full max-w-2xl">
        {isLoading ? (
          <div className="text-sm text-gray-400">{t("Loading...")}</div>
        ) : token ? (
          <>
            <div className="font-gilroy font-bold mb-2">{t("Access Token")}</div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  className="text-sm bg-gray-100 text-gray-600 border rounded-sm outline-none h-8 px-2 font-mono w-[560px]"
                  value={token.token}
                  onClick={(e) => e.currentTarget.select()}
                  readOnly
                />
                <CopyToClipboard text={token.token} onCopy={handleCopied}>
                  <button className="relative flex items-center justify-center border bg-white h-8 w-8 flex-shrink-0 rounded-sm group shadow-sm">
                    <ClipboardCopyIcon className="h-4 w-4 group-hover:text-blue" />
                    <div className="hidden group-hover:block absolute bg-black text-white whitespace-nowrap text-xs top-9 right-4 py-1 px-2 rounded-md rounded-tr-none">
                      {isCopied ? t("Copied") : t("Copy to clipboard")}
                    </div>
                  </button>
                </CopyToClipboard>
                <button
                  className="flex items-center justify-center border bg-white h-8 w-8 flex-shrink-0 rounded-sm group shadow-sm text-gray-400 hover:text-red transition-colors"
                  onClick={handleRevokeToken}
                  disabled={isRevoking}
                  title={t("Revoke token")}
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
              <div className="text-xs text-gray-400">{t("Created")} {moment(token.createdAt).fromNow()}</div>
            </div>
          </>
        ) : (
          <button
            className="btn btn-blue flex items-center gap-2 w-fit"
            onClick={handleCreateToken}
            disabled={isCreating}
          >
            <PlusIcon className="w-5 h-5" />
            {isCreating ? t("Creating...") : t("Generate Token")}
          </button>
        )}
      </div>
    </div>
  );
}

export default SettingsDeveloper;
